// ═══════════════════════════════════════════════════════════════════════════════
// Main menu structure — paired employer / helper accordion navigation
// ═══════════════════════════════════════════════════════════════════════════════

import type { Location } from 'react-router-dom';

export type MainMenuSubItem = {
  id: string;
  labelKey: string;
  to: string;
  isActive: (location: Location) => boolean;
  badgeCount?: number;
};

export type MainMenuGroupItem = {
  id: string;
  type: 'group';
  labelKey: string;
  matchesSection: (location: Location) => boolean;
  children: MainMenuSubItem[];
  badgeCount?: number;
};

export type MainMenuLinkItem = {
  id: string;
  type: 'link';
  labelKey: string;
  to: string;
  isActive: (location: Location) => boolean;
};

export type MainMenuItem = MainMenuGroupItem | MainMenuLinkItem;

const pathnameStartsWith = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

const isReceiptListPath = (pathname: string): boolean => {
  if (pathname === '/receipts') return true;
  if (!pathname.startsWith('/receipts/')) return false;
  if (pathname.startsWith('/receipts/analyze')) return false;
  return /^\/receipts\/[^/]+$/.test(pathname);
};

const isHelperTodayTab = (location: Location, tab: 'today' | 'future' | 'overdue' | 'history'): boolean => {
  if (location.pathname !== '/today') return false;
  const tabParam = new URLSearchParams(location.search).get('tab');
  if (tab === 'today') return tabParam === null || tabParam === 'today';
  return tabParam === tab;
};

const employerHomeChildren: MainMenuSubItem[] = [
  {
    id: 'employer-home-dashboard',
    labelKey: 'nav.home',
    to: '/dashboard',
    isActive: ({ pathname }) => pathname === '/dashboard',
  },
  {
    id: 'employer-home-system-messages',
    labelKey: 'systemMessage.menu',
    to: '/system-messages',
    isActive: ({ pathname }) => pathnameStartsWith(pathname, '/system-messages'),
  },
  {
    id: 'employer-home-pin-settings',
    labelKey: 'settings.menuPinMessages',
    to: '/pin-messages',
    isActive: ({ pathname }) => pathname === '/pin-messages',
  },
  {
    id: 'employer-home-pin-add',
    labelKey: 'pinMessage.action.add',
    to: '/pin-messages/new',
    isActive: ({ pathname }) => pathname === '/pin-messages/new',
  },
];

const helperHomeChildren: MainMenuSubItem[] = [
  {
    id: 'helper-home-main',
    labelKey: 'nav.home',
    to: '/home',
    isActive: ({ pathname }) => pathname === '/home',
  },
  {
    id: 'helper-home-system-messages',
    labelKey: 'systemMessage.menu',
    to: '/system-messages',
    isActive: ({ pathname }) => pathnameStartsWith(pathname, '/system-messages'),
  },
];

const employerTasksChildren: MainMenuSubItem[] = [
  {
    id: 'employer-tasks-overview',
    labelKey: 'settings.menuOverview',
    to: '/tasks',
    isActive: ({ pathname }) => pathname === '/tasks',
  },
  {
    id: 'employer-tasks-edit-all',
    labelKey: 'task.list.menuEditAllTasks',
    to: '/tasks/edit-all',
    isActive: ({ pathname }) => pathname === '/tasks/edit-all',
  },
  {
    id: 'employer-tasks-pending-review',
    labelKey: 'task.list.pendingReviewNav',
    to: '/tasks/pending-review',
    isActive: ({ pathname }) => pathname === '/tasks/pending-review',
  },
  {
    id: 'employer-tasks-history',
    labelKey: 'task.list.menuHistoryTasks',
    to: '/tasks/history',
    isActive: ({ pathname }) => pathname === '/tasks/history',
  },
  {
    id: 'employer-tasks-future',
    labelKey: 'task.list.menuFutureTasks',
    to: '/tasks/future',
    isActive: ({ pathname }) => pathname === '/tasks/future',
  },
  {
    id: 'employer-tasks-overdue',
    labelKey: 'task.list.overdueCardLabel',
    to: '/tasks/overdue',
    isActive: ({ pathname }) => pathname === '/tasks/overdue',
  },
  {
    id: 'employer-tasks-latest-stock',
    labelKey: 'task.review.latestHomeStockTitle',
    to: '/stock/latest-home-stock',
    isActive: ({ pathname }) => pathname === '/stock/latest-home-stock',
  },
];

const employerMoneyChildren: MainMenuSubItem[] = [
  {
    id: 'employer-money-overview',
    labelKey: 'settings.menuOverview',
    to: '/settlements',
    isActive: ({ pathname }) => pathname === '/settlements',
  },
  {
    id: 'employer-money-add',
    labelKey: 'budget.form.createTitle',
    to: '/budgets/new',
    isActive: ({ pathname }) => pathname === '/budgets/new',
  },
  {
    id: 'employer-money-pending-review',
    labelKey: 'settlement.pendingReviewPageTitle',
    to: '/settlements/pending-review',
    isActive: ({ pathname }) => pathname === '/settlements/pending-review',
  },
  {
    id: 'employer-money-receipts',
    labelKey: 'settings.menuReceiptBrowse',
    to: '/receipts',
    isActive: ({ pathname }) => isReceiptListPath(pathname),
  },
];

const helperSettingsChildren: MainMenuSubItem[] = [
  {
    id: 'settings-account',
    labelKey: 'settings.menuAccountSettings',
    to: '/settings',
    isActive: ({ pathname }) => pathname === '/settings',
  },
  {
    id: 'settings-pairing',
    labelKey: 'settings.menuPairingStatus',
    to: '/pairing/status',
    isActive: ({ pathname }) => pathname === '/pairing/status',
  },
  {
    id: 'settings-usage',
    labelKey: 'usage.pageTitle',
    to: '/settings/usage',
    isActive: ({ pathname }) => pathname === '/settings/usage',
  },
];

const inviteReferrerMenuItem: MainMenuSubItem = {
  id: 'settings-invite-referrer',
  labelKey: 'inviteReferrer.menuItem',
  to: '/settings/invite-referrer',
  isActive: ({ pathname }) => pathname === '/settings/invite-referrer',
};

const buildEmployerSettingsChildren = (showInviteReferrer: boolean): MainMenuSubItem[] => [
  {
    id: 'settings-account',
    labelKey: 'settings.menuAccountSettings',
    to: '/settings',
    isActive: ({ pathname }) => pathname === '/settings',
  },
  {
    id: 'settings-pairing',
    labelKey: 'settings.menuPairingStatus',
    to: '/pairing/status',
    isActive: ({ pathname }) => pathname === '/pairing/status',
  },
  {
    id: 'settings-usage',
    labelKey: 'usage.pageTitle',
    to: '/settings/usage',
    isActive: ({ pathname }) => pathname === '/settings/usage',
  },
  {
    id: 'settings-subscription',
    labelKey: 'settings.menuSubscriptionAndInvite',
    to: '/settings/subscription',
    isActive: ({ pathname }) => pathname === '/settings/subscription',
  },
  ...(showInviteReferrer ? [inviteReferrerMenuItem] : []),
];

const buildHelperSettingsChildren = (showInviteReferrer: boolean): MainMenuSubItem[] => [
  ...helperSettingsChildren,
  ...(showInviteReferrer ? [inviteReferrerMenuItem] : []),
];

const helperTasksChildren: MainMenuSubItem[] = [
  {
    id: 'helper-tasks-today',
    labelKey: 'settings.menuTodayTask',
    to: '/today',
    isActive: (location) => isHelperTodayTab(location, 'today'),
  },
  {
    id: 'helper-tasks-future',
    labelKey: 'task.todo.tabFuture',
    to: '/today?tab=future',
    isActive: (location) => isHelperTodayTab(location, 'future'),
  },
  {
    id: 'helper-tasks-overdue',
    labelKey: 'task.todo.tabOverdue',
    to: '/today?tab=overdue',
    isActive: (location) => isHelperTodayTab(location, 'overdue'),
  },
  {
    id: 'helper-tasks-history',
    labelKey: 'task.todo.tabHistory',
    to: '/today?tab=history',
    isActive: (location) => isHelperTodayTab(location, 'history'),
  },
  {
    id: 'helper-tasks-latest-stock',
    labelKey: 'task.review.latestHomeStockTitle',
    to: '/stock/latest-home-stock',
    isActive: ({ pathname }) => pathname === '/stock/latest-home-stock',
  },
];

const helperMoneyChildren: MainMenuSubItem[] = [
  {
    id: 'helper-money-overview',
    labelKey: 'settings.menuOverview',
    to: '/settlements',
    isActive: ({ pathname }) => pathname === '/settlements',
  },
  {
    id: 'helper-money-scan',
    labelKey: 'settings.menuReceipts',
    to: '/receipts/analyze',
    isActive: ({ pathname }) => pathname === '/receipts/analyze',
  },
  {
    id: 'helper-money-receipts',
    labelKey: 'settings.menuReceiptBrowse',
    to: '/receipts',
    isActive: ({ pathname }) => isReceiptListPath(pathname),
  },
  {
    id: 'helper-money-history',
    labelKey: 'settlement.helperHistoryPageTitle',
    to: '/settlements/history',
    isActive: ({ pathname }) => pathname === '/settlements/history',
  },
];

export const buildEmployerPairedMenu = (
  showInviteReferrer = false,
  showPreviousPairingImport = false,
): MainMenuItem[] => [
  {
    id: 'employer-home',
    type: 'group',
    labelKey: 'nav.home',
    matchesSection: ({ pathname }) =>
      pathname === '/dashboard' ||
      pathnameStartsWith(pathname, '/pin-messages') ||
      pathnameStartsWith(pathname, '/system-messages'),
    children: employerHomeChildren,
  },
  {
    id: 'employer-tasks',
    type: 'group',
    labelKey: 'settings.menuTasksEmployer',
    matchesSection: ({ pathname }) =>
      pathnameStartsWith(pathname, '/tasks') ||
      pathname === '/stock/latest-home-stock' ||
      pathnameStartsWith(pathname, '/task-instances'),
    children: employerTasksChildren,
  },
  {
    id: 'employer-money',
    type: 'group',
    labelKey: 'settings.menuMoneyGivenSettlement',
    matchesSection: ({ pathname }) =>
      pathnameStartsWith(pathname, '/settlements') ||
      pathnameStartsWith(pathname, '/budgets') ||
      pathnameStartsWith(pathname, '/receipts'),
    children: employerMoneyChildren,
  },
  {
    id: 'employer-family',
    type: 'link',
    labelKey: 'settings.menuFamily',
    to: '/family-members',
    isActive: ({ pathname }) => pathnameStartsWith(pathname, '/family-members'),
  },
  {
    id: 'employer-settings',
    type: 'group',
    labelKey: 'settings.menuSettings',
    matchesSection: ({ pathname }) =>
      pathname === '/settings' ||
      pathname === '/settings/usage' ||
      pathname === '/settings/subscription' ||
      pathname === '/settings/invite-referrer' ||
      pathname === '/pairing/status',
    children: buildEmployerSettingsChildren(showInviteReferrer),
  },
  {
    id: 'employer-faq',
    type: 'link',
    labelKey: 'home.linkFaq',
    to: '/faq',
    isActive: ({ pathname }) => pathname === '/faq',
  },
  ...(showPreviousPairingImport
    ? [
        {
          id: 'employer-import-previous-pairing',
          type: 'link' as const,
          labelKey: 'settings.menuImportPreviousPairing',
          to: '/pairing/import-previous',
          isActive: ({ pathname }: Location) => pathname === '/pairing/import-previous',
        },
      ]
    : []),
];

export const buildHelperPairedMenu = (showInviteReferrer = false): MainMenuItem[] => [
  {
    id: 'helper-home',
    type: 'group',
    labelKey: 'nav.home',
    matchesSection: ({ pathname }) =>
      pathname === '/home' || pathnameStartsWith(pathname, '/system-messages'),
    children: helperHomeChildren,
  },
  {
    id: 'helper-tasks',
    type: 'group',
    labelKey: 'settings.menuTasksHelper',
    matchesSection: ({ pathname }) =>
      pathname === '/today' ||
      pathname === '/stock/latest-home-stock' ||
      pathnameStartsWith(pathname, '/tasks') ||
      pathnameStartsWith(pathname, '/task-instances'),
    children: helperTasksChildren,
  },
  {
    id: 'helper-money',
    type: 'group',
    labelKey: 'settings.menuSettlements',
    matchesSection: ({ pathname }) =>
      pathnameStartsWith(pathname, '/settlements') || pathnameStartsWith(pathname, '/receipts'),
    children: helperMoneyChildren,
  },
  {
    id: 'helper-family',
    type: 'link',
    labelKey: 'settings.menuFamily',
    to: '/family-members',
    isActive: ({ pathname }) => pathnameStartsWith(pathname, '/family-members'),
  },
  {
    id: 'helper-settings',
    type: 'group',
    labelKey: 'settings.menuSettings',
    matchesSection: ({ pathname }) =>
      pathname === '/settings' ||
      pathname === '/settings/usage' ||
      pathname === '/settings/invite-referrer' ||
      pathname === '/pairing/status',
    children: buildHelperSettingsChildren(showInviteReferrer),
  },
  {
    id: 'helper-faq',
    type: 'link',
    labelKey: 'home.linkFaq',
    to: '/faq',
    isActive: ({ pathname }) => pathname === '/faq',
  },
];

const SYSTEM_MESSAGE_MENU_CHILD_IDS = new Set([
  'employer-home-system-messages',
  'helper-home-system-messages',
]);

export const applyUnreadSystemMessageBadges = (
  items: MainMenuItem[],
  unreadCount: number,
): MainMenuItem[] => {
  if (unreadCount <= 0) {
    return items;
  }

  return items.map((item) => {
    if (item.type !== 'group') {
      return item;
    }

    const children = item.children.map((child) => {
      if (!SYSTEM_MESSAGE_MENU_CHILD_IDS.has(child.id)) {
        return child;
      }
      return { ...child, badgeCount: unreadCount };
    });
    const hasUnreadSystemMessages = children.some(
      (child) => SYSTEM_MESSAGE_MENU_CHILD_IDS.has(child.id) && (child.badgeCount ?? 0) > 0,
    );
    if (!hasUnreadSystemMessages) {
      return { ...item, children };
    }
    return { ...item, badgeCount: unreadCount, children };
  });
};

export const getAutoExpandedSectionIds = (items: MainMenuItem[], location: Location): Set<string> => {
  const expanded = new Set<string>();
  items.forEach((item) => {
    if (item.type !== 'group') return;
    if (item.matchesSection(location)) {
      expanded.add(item.id);
    }
  });
  return expanded;
};
